/**
 * useIdleTimeout — fires `onTimeout` after `timeoutMs` of no user activity.
 *
 * Industry-standard pattern for SaaS dashboards (Microsoft 365, Google
 * Workspace, Stripe, Notion all do this): if a user walks away from
 * their laptop, we shouldn't leave their authenticated tab open
 * indefinitely. After 30 minutes of inactivity we auto-redirect to
 * /login?reason=idle.
 *
 * Activity = any of mousemove, mousedown, keydown, touchstart, scroll,
 * wheel. The listeners use { passive: true } so they're effectively
 * free even though they fire constantly.
 *
 * Implementation notes:
 *  • setTimeout is reset on every event (debounced via an internal
 *    timestamp guard so we don't reschedule 60×/sec while a user is
 *    actively typing).
 *  • The warning callback fires `warnAtMs` before timeout so the UI can
 *    show a toast / modal — pass undefined to skip.
 *  • Pure client-side; backend session enforcement is independent
 *    (Entra token exp + tokens_revoked_before).
 */
import { useEffect, useRef } from 'react';
const ACTIVITY_EVENTS = [
    'mousemove',
    'mousedown',
    'keydown',
    'touchstart',
    'scroll',
    'wheel',
];
// Don't reschedule the timeout for activity bursts closer together
// than this; cheap throttle so a moving mouse doesn't fire 60 events
// per second's worth of setTimeout work.
const RESCHEDULE_THROTTLE_MS = 1_000;
// Shared "idle sign-out deadline" (epoch ms) published by the single active
// useIdleTimeout (the AppShell). The SessionIndicator chip reads it so it can
// show the REAL time until the user is signed out for inactivity — the clock
// that actually logs them out — rather than the auto-renewing token clock.
// Null when the hook is disabled / unmounted (e.g. pre-login).
let idleDeadlineMs = null;
/** Time until idle sign-out, as Unix epoch seconds, or null when there's no
 *  active idle timer. Shape matches `useSessionTimer`'s `getExpiry`. */
export function getIdleExpiryEpochSeconds() {
    return idleDeadlineMs === null ? null : Math.floor(idleDeadlineMs / 1000);
}
export function useIdleTimeout({ timeoutMs, onTimeout, warnAtMs, onWarn, disabled = false, }) {
    // Refs so the schedule closure always sees the latest callbacks
    // without us having to add them to the effect's deps (which would
    // tear down the listeners on every callback change).
    const onTimeoutRef = useRef(onTimeout);
    const onWarnRef = useRef(onWarn);
    const lastResetRef = useRef(0);
    const timeoutIdRef = useRef(null);
    const warnIdRef = useRef(null);
    // Seed the shared idle deadline during render (this hook's host renders
    // BEFORE its children, so the SessionIndicator chip reads a value on first
    // paint instead of waiting for this effect + a 30s tick). Singleton +
    // idempotent: only sets when unset; the effect below keeps it fresh.
    if (!disabled && idleDeadlineMs === null) {
        idleDeadlineMs = Date.now() + timeoutMs;
    }
    useEffect(() => {
        onTimeoutRef.current = onTimeout;
    }, [onTimeout]);
    useEffect(() => {
        onWarnRef.current = onWarn;
    }, [onWarn]);
    useEffect(() => {
        if (disabled || typeof window === 'undefined')
            return;
        const clearTimers = () => {
            if (timeoutIdRef.current !== null) {
                clearTimeout(timeoutIdRef.current);
                timeoutIdRef.current = null;
            }
            if (warnIdRef.current !== null) {
                clearTimeout(warnIdRef.current);
                warnIdRef.current = null;
            }
        };
        const schedule = () => {
            clearTimers();
            // Publish the deadline so the SessionIndicator can show the real
            // time-until-idle-sign-out (reset here on every activity burst).
            idleDeadlineMs = Date.now() + timeoutMs;
            timeoutIdRef.current = setTimeout(() => {
                onTimeoutRef.current();
            }, timeoutMs);
            if (warnAtMs !== undefined && warnAtMs > 0 && warnAtMs < timeoutMs) {
                warnIdRef.current = setTimeout(() => {
                    onWarnRef.current?.();
                }, timeoutMs - warnAtMs);
            }
        };
        const onActivity = () => {
            const now = Date.now();
            if (now - lastResetRef.current < RESCHEDULE_THROTTLE_MS)
                return;
            lastResetRef.current = now;
            schedule();
        };
        // Kick off the first window.
        schedule();
        for (const evt of ACTIVITY_EVENTS) {
            window.addEventListener(evt, onActivity, { passive: true });
        }
        return () => {
            clearTimers();
            idleDeadlineMs = null; // no active idle timer → chip hides
            for (const evt of ACTIVITY_EVENTS) {
                window.removeEventListener(evt, onActivity);
            }
        };
    }, [timeoutMs, warnAtMs, disabled]);
}
